import { useState } from 'react'
import {
  ActionIcon,
  Alert,
  Badge,
  Button,
  Card,
  Center,
  Group,
  Loader,
  Menu,
  Modal,
  Select,
  Stack,
  Switch,
  Text,
} from '@mantine/core'
import {
  IconCalendarRepeat,
  IconDotsVertical,
  IconLock,
  IconPencil,
  IconPlus,
  IconTrash,
} from '@tabler/icons-react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { notifications } from '@mantine/notifications'
import type { RecurringTemplateDto } from '@ledokol/contracts'
import { fetchProjects } from '../../shared/api/projects'
import { deleteRecurring, fetchRecurring, updateRecurring } from '../../shared/api/recurring'
import { useAuthStore } from '../auth/store'
import { RecurringFormModal } from './recurring-form-modal'

const WEEKDAY_LABEL: Record<string, string> = {
  MO: 'Понедельник', TU: 'Вторник', WE: 'Среда', TH: 'Четверг', FR: 'Пятница', SA: 'Суббота', SU: 'Воскресенье',
}

function describeSchedule(t: RecurringTemplateDto): string {
  const o = t.due_offset
  if (o.kind === 'weekday') return `Еженедельно · ${WEEKDAY_LABEL[o.value] ?? o.value} · ${o.time}`
  if (o.kind === 'business_days_after_month_end') return `Ежемесячно · ${o.value}-й раб. день · ${o.time}`
  return `Событийно · через ${o.value} раб. дн. · ${o.time}`
}

export function RecurringTab() {
  const { user } = useAuthStore()
  const qc = useQueryClient()
  // employee/observer не управляют регулярками — кнопка/действия скрыты (сервер тоже 403/404)
  const canManage = !!user && ['admin', 'director', 'head', 'account'].includes(user.role)

  const projectsQ = useQuery({ queryKey: ['projects'], queryFn: () => fetchProjects() })
  const [projectId, setProjectId] = useState<string | null>(null)

  const [formOpen, setFormOpen] = useState(false)
  const [editing, setEditing] = useState<RecurringTemplateDto | null>(null)
  const [deleteTarget, setDeleteTarget] = useState<RecurringTemplateDto | null>(null)

  const listQ = useQuery({
    queryKey: ['recurring', projectId],
    queryFn: () => fetchRecurring(projectId!),
    // employee/observer не управляют регулярками — даже list на бэкенде 403/404;
    // не дёргаем его, показываем понятный отказ вместо сломанного пустого списка
    enabled: !!projectId && canManage,
  })

  const toggleActive = useMutation({
    mutationFn: (t: RecurringTemplateDto) => updateRecurring(t.id, { is_active: !t.is_active }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['recurring', projectId] }),
    onError: () => notifications.show({ color: 'red', title: 'Ошибка', message: 'Не удалось изменить статус' }),
  })

  const remove = useMutation({
    mutationFn: (id: string) => deleteRecurring(id),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['recurring', projectId] })
      notifications.show({ color: 'teal', title: 'Удалено', message: 'Регулярка удалена' })
      setDeleteTarget(null)
    },
    onError: () => notifications.show({ color: 'red', title: 'Ошибка', message: 'Не удалось удалить' }),
  })

  const projectOptions =
    projectsQ.data?.map((p) => ({ value: p.id, label: p.name })) ?? []

  if (!canManage) {
    return (
      <Alert color="gray" icon={<IconLock size={18} />} variant="light" title="Только для управляющих ролей">
        Управление регулярными задачами доступно администратору, директору,
        руководителю отдела или ответственному по проекту.
      </Alert>
    )
  }

  return (
    <Stack gap="md">
      <Group justify="space-between" align="flex-end">
        <Select
          label="Проект"
          placeholder="Выберите рекламодателя"
          data={projectOptions}
          value={projectId}
          onChange={setProjectId}
          searchable
          w={320}
        />
        {canManage && projectId && (
          <Button
            leftSection={<IconPlus size={16} />}
            onClick={() => {
              setEditing(null)
              setFormOpen(true)
            }}
          >
            Новая регулярка
          </Button>
        )}
      </Group>

      {!projectId && (
        <Card withBorder p="xl" radius="md">
          <Center>
            <Text c="dimmed" size="sm">
              Выберите проект, чтобы увидеть его регулярные задачи
            </Text>
          </Center>
        </Card>
      )}

      {projectId && listQ.isLoading && (
        <Center mih={160}>
          <Loader />
        </Center>
      )}

      {projectId && listQ.data && listQ.data.length === 0 && (
        <Card withBorder p="xl" radius="md">
          <Center>
            <Stack align="center" gap="xs">
              <IconCalendarRepeat size={40} color="var(--mantine-color-gray-5)" />
              <Text c="dimmed" size="sm">
                У этого проекта пока нет регулярных задач
              </Text>
            </Stack>
          </Center>
        </Card>
      )}

      {projectId &&
        listQ.data?.map((t) => (
          <Card key={t.id} withBorder p="md" radius="md">
            <Group justify="space-between" wrap="nowrap" align="flex-start">
              <Stack gap={4} style={{ minWidth: 0 }}>
                <Group gap="xs">
                  <Text fw={600} truncate>
                    {t.title_template}
                  </Text>
                  <Badge color={t.is_active ? 'teal' : 'gray'} variant="light" size="sm">
                    {t.is_active ? 'Активна' : 'Остановлена'}
                  </Badge>
                </Group>
                <Text size="sm" c="dimmed">
                  {describeSchedule(t)} · создаётся за {t.create_days_before} дн.
                </Text>
                <Group gap="xs">
                  <Text size="xs" c="dimmed">
                    Исполнитель: {t.assignee?.full_name ?? 'не назначен'}
                  </Text>
                  {t.last_generated_at && (
                    <Text size="xs" c="dimmed">
                      · последняя генерация {new Date(t.last_generated_at).toLocaleDateString('ru-RU')}
                    </Text>
                  )}
                </Group>
              </Stack>

              {canManage && (
                <Group gap="xs" wrap="nowrap">
                  <Switch
                    checked={t.is_active}
                    onChange={() => toggleActive.mutate(t)}
                    size="sm"
                    aria-label="Активность"
                  />
                  <Menu position="bottom-end" shadow="md">
                    <Menu.Target>
                      <ActionIcon variant="subtle" color="gray">
                        <IconDotsVertical size={18} />
                      </ActionIcon>
                    </Menu.Target>
                    <Menu.Dropdown>
                      <Menu.Item
                        leftSection={<IconPencil size={16} />}
                        onClick={() => {
                          setEditing(t)
                          setFormOpen(true)
                        }}
                      >
                        Редактировать
                      </Menu.Item>
                      <Menu.Item
                        leftSection={<IconTrash size={16} />}
                        color="red"
                        onClick={() => setDeleteTarget(t)}
                      >
                        Удалить
                      </Menu.Item>
                    </Menu.Dropdown>
                  </Menu>
                </Group>
              )}
            </Group>
          </Card>
        ))}

      {projectId && (
        <RecurringFormModal
          projectId={projectId}
          opened={formOpen}
          onClose={() => setFormOpen(false)}
          editing={editing}
        />
      )}

      <Modal
        opened={!!deleteTarget}
        onClose={() => setDeleteTarget(null)}
        title={<Text fw={700}>Удалить регулярку?</Text>}
        size="sm"
      >
        <Stack gap="md">
          <Text size="sm">
            «{deleteTarget?.title_template}» — конфиг будет удалён. Уже созданные задачи останутся.
          </Text>
          <Group justify="flex-end">
            <Button variant="default" onClick={() => setDeleteTarget(null)}>
              Отмена
            </Button>
            <Button
              color="red"
              loading={remove.isPending}
              onClick={() => deleteTarget && remove.mutate(deleteTarget.id)}
            >
              Удалить
            </Button>
          </Group>
        </Stack>
      </Modal>
    </Stack>
  )
}
